import { createClient } from "@supabase/supabase-js";

/**
 * The Supabase client.
 *
 * The publishable (anon) key is compiled into the bundle, which is by design:
 * it identifies the project and grants nothing on its own. Row level security
 * is what protects the data — and since the admin-role migration, a token
 * without an `admin` or `agent` role sees no rows at all.
 *
 * A service_role/secret key must never appear here. It belongs in Edge Function
 * secrets only.
 */

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    "VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set. Copy .env.example to apps/web/.env.local.",
  );
}

export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

/**
 * Throw on a PostgREST error, otherwise hand back the rows as `T`.
 *
 * The cast is deliberate. Without generated database types, postgrest-js infers
 * embedded relations as arrays (`rep: {full_name}[]`) even for a to-one join,
 * which does not match our hand-written domain types. The shapes in
 * @roof-hawk/core are the source of truth here; the column lists that feed them
 * all live in queries.ts, so a mismatch is a one-file fix.
 */
export function unwrap<T>(result: {
  data: unknown;
  error: { message: string } | null;
}): T {
  if (result.error) throw new Error(result.error.message);
  return (result.data ?? []) as T;
}
